// 1.3.73: MVP 多租户架构 - 组织类型定义

/**
 * 组织类型
 */
export type OrgType = 'personal' | 'business';

/**
 * 套餐类型
 */
export type PlanType = 'free' | 'individual' | 'standard';

/**
 * 成员角色
 */
export type MemberRole = 'owner' | 'member';

/**
 * 成员状态
 */
export type MemberStatus = 'active' | 'pending';

/**
 * 组织基础信息
 */
export interface Organization {
  id: string;
  name: string;
  slug: string;
  org_type: OrgType;
  plan_type: PlanType;
  max_members: number;
  max_storage_gb: number;
  public_link_quota: number;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

/**
 * 组织成员信息
 */
export interface OrganizationMember {
  id: string;
  user_id: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
  joined_at: string;
  invited_by?: string;
  invited_at?: string;
}

/**
 * 用户组织列表项（包含成员数和当前角色）
 */
export interface UserOrganization {
  id: string;
  name: string;
  slug: string;
  org_type: OrgType;
  plan_type: PlanType;
  max_members: number;
  current_members: number;
  role: MemberRole;
  is_current: boolean;
}

/**
 * 组织详情（包含成员列表）
 */
export interface OrganizationDetails {
  organization: Organization;
  members: OrganizationMember[];
  current_user_role: MemberRole;
}

/**
 * 组织内的知识库列表项
 */
export interface OrganizationKnowledgeBase {
  id: string;
  name: string;
  description?: string;
  is_shared: boolean;
  is_mine: boolean;
  owner_email: string;
  document_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * API 响应结果
 */
export interface OrganizationApiResult {
  success: boolean;
  message?: string;
  organization_id?: string;
  member_id?: string;
  slug?: string;
}

/**
 * 套餐配置
 */
export interface PlanConfig {
  type: PlanType;
  name: string;
  price: number;         // 月费（人民币）
  per_user?: boolean;    // 是否按人数计费
  max_members: number;
  storage_gb: number;
  public_link_quota: number;
  features: string[];
}

/**
 * 套餐配置常量
 */
export const PLAN_CONFIGS: Record<PlanType, PlanConfig> = {
  free: {
    type: 'free',
    name: 'Free',
    price: 0,
    max_members: 1,
    storage_gb: 0,
    public_link_quota: 0,
    features: ['基础对话功能', '无公开链接'],
  },
  individual: {
    type: 'individual',
    name: 'Individual',
    price: 2500,
    max_members: 1,
    storage_gb: 1,
    public_link_quota: 5000,
    features: ['1人使用', '1GB存储', '5,000次公开链接/月', '无限内部对话'],
  },
  standard: {
    type: 'standard',
    name: 'Standard',
    price: 2300,
    per_user: true,
    max_members: 50,
    storage_gb: 10,
    public_link_quota: 10000,
    features: ['2-50人团队', '10GB存储', '10,000次公开链接/月', '无限内部对话', '共享知识库'],
  },
};
